import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      features: "Features",
      location: "Location",
      admin: "Admin",
      bookNow: "Book Now",
    },
    // Hero Section
    hero: {
      tagline: "Trusted Luggage Storage in Bari",
      title1: "Travel Light,",
      title2: "Explore Freely",
      description: "Secure luggage storage in the heart of Bari. Drop your bags, pick up your freedom, and discover the beauty of Puglia.",
      learnMore: "Learn More",
      perBagDay: "per bag/day",
      perBagHour: "per bag/hour",
      support: "Support",
    },
    // Features Section
    features: {
      tagline: "Why Choose Us",
      title: "Safe, Simple & Secure",
      secure: {
        title: "100% Secure",
        description: "Your luggage is stored in a locked, monitored facility with insurance coverage for peace of mind.",
      },
      flexible: {
        title: "Flexible Hours",
        description: "Store your bags for hours or days. Pay only for the time you need with our flexible pricing.",
      },
      central: {
        title: "Central Location",
        description: "Located in the heart of Bari, just minutes from the train station and historic old town.",
      },
    },
    // Booking Form
    booking: {
      tagline: "Book Your Storage",
      title: "Reserve Your Spot",
      subtitle: "Fill in your details and we'll take care of the rest",
      contactInfo: "Contact Information",
      fullName: "Full Name",
      fullNamePlaceholder: "Enter your full name",
      email: "Email Address",
      emailPlaceholder: "your@email.com",
      mobile: "Mobile Number",
      whatsapp: "WhatsApp Number",
      phonePlaceholder: "+39 xxx xxx xxxx",
      storageDetails: "Storage Details",
      numberOfBags: "Number of Bags",
      selectBags: "Select number of bags",
      bag: "bag",
      bags: "bags",
      pickupDate: "Pickup Date",
      pickupTime: "Pickup Time",
      dropoffDate: "Drop-off Date",
      dropoffTime: "Drop-off Time",
      selectDate: "Select date",
      selectTime: "Select time",
      paymentMethod: "Payment Method",
      payAtLocation: "Pay at Location",
      payAtLocationDesc: "Cash or card payment on arrival",
      totalPrice: "Total Price",
      calculating: "Calculating price...",
      confirmBooking: "Confirm Booking",
      processing: "Processing...",
      pricingType: "Pricing Type",
      pricingAuto: "Auto",
      pricingAutoDesc: "Best rate",
      perHour: "per hour",
      perDay: "per day",
      sameAsMobile: "Same as mobile",
    },
    // Location Section
    location: {
      tagline: "Find Us",
      title: "Our Location",
      openMaps: "Open in Google Maps",
    },
    // Footer
    footer: {
      description: "Your trusted luggage storage service in the heart of Bari, Italy.",
      contact: "Contact",
      pricing: "Pricing",
      perDay: "per bag per day",
      perHour: "per bag per hour",
      hourlyNote: "*Hourly rate for same-day storage",
      rights: "All rights reserved.",
    },
    // Booking Confirmation
    confirmation: {
      title: "Booking Confirmed!",
      subtitle: "You're all set to explore Bari hands-free!",
      reference: "Booking Reference",
      customerDetails: "Customer Details",
      storageDetails: "Storage Details",
      numberOfBags: "Number of Bags",
      pricingType: "Pricing Type",
      pickup: "Pickup",
      dropoff: "Drop-off",
      totalPrice: "Total Price",
      payAtLocation: "Pay at Location",
      location: "Location",
      openMaps: "Open in Google Maps",
      shareWhatsApp: "Share your booking via WhatsApp",
      contactWhatsApp: "Contact Us on WhatsApp",
      backHome: "Back to Home",
      notFound: "Booking Not Found",
      notFoundDesc: "We couldn't find this booking. Please check your booking ID.",
      loading: "Loading your booking...",
    },
    // Admin
    admin: {
      login: {
        title: "Welcome Back",
        subtitle: "Sign in to manage bookings",
        createTitle: "Create Account",
        createSubtitle: "Set up your admin account",
        fullName: "Full Name",
        email: "Email Address",
        password: "Password",
        signIn: "Sign In",
        createAccount: "Create Account",
        signingIn: "Signing in...",
        creating: "Creating account...",
        needAccount: "Need an account? Register",
        haveAccount: "Already have an account? Sign in",
        backToWebsite: "Back to Website",
        portal: "Admin Portal",
      },
      dashboard: {
        title: "Dashboard",
        bookings: "Bookings",
        settings: "Settings",
        overview: "Overview of your business",
        manageBookings: "Manage all bookings",
        configureSettings: "Configure your business",
        refresh: "Refresh",
        totalBookings: "Total Bookings",
        pending: "Pending",
        confirmed: "Confirmed",
        revenue: "Revenue",
        recentBookings: "Recent Bookings",
        allBookings: "All Bookings",
        viewAll: "View All",
        filterStatus: "Filter by status",
        all: "All Bookings",
        completed: "Completed",
        cancelled: "Cancelled",
        customer: "Customer",
        price: "Price",
        status: "Status",
        actions: "Actions",
        noBookings: "No bookings found",
        bookingDetails: "Booking Details",
        contactWhatsApp: "Contact via WhatsApp",
        updateStatus: "Update Status",
        confirm: "Confirm",
        complete: "Complete",
        cancel: "Cancel",
        loggedInAs: "Logged in as",
        logout: "Logout",
        companyLogo: "Company Logo",
        currentLogo: "Current Logo",
        noLogo: "No logo",
        uploadInfo: "PNG, JPEG, WebP or SVG. Max 2MB.",
        removeLogo: "Remove Logo",
        logoUploaded: "Logo uploaded successfully!",
        logoDeleted: "Logo deleted",
      },
    },
    // Common
    common: {
      hours: "hours",
      hour: "hour",
      days: "days",
      day: "day",
      daily: "daily",
      hourly: "hourly",
    },
  },
  it: {
    // Navigation
    nav: {
      features: "Caratteristiche",
      location: "Posizione",
      admin: "Admin",
      bookNow: "Prenota Ora",
    },
    // Hero Section
    hero: {
      tagline: "Deposito Bagagli Affidabile a Bari",
      title1: "Viaggia Leggero,",
      title2: "Esplora Liberamente",
      description: "Deposito bagagli sicuro nel cuore di Bari. Lascia le tue valigie, riprendi la tua libertà e scopri la bellezza della Puglia.",
      learnMore: "Scopri di Più",
      perBagDay: "per borsa/giorno",
      perBagHour: "per borsa/ora",
      support: "Supporto",
    },
    // Features Section
    features: {
      tagline: "Perché Sceglierci",
      title: "Sicuro, Semplice e Protetto",
      secure: {
        title: "100% Sicuro",
        description: "I tuoi bagagli sono conservati in una struttura chiusa e monitorata con copertura assicurativa per la tua tranquillità.",
      },
      flexible: {
        title: "Orari Flessibili",
        description: "Deposita i tuoi bagagli per ore o giorni. Paga solo per il tempo necessario con i nostri prezzi flessibili.",
      },
      central: {
        title: "Posizione Centrale",
        description: "Situato nel cuore di Bari, a pochi minuti dalla stazione ferroviaria e dal centro storico.",
      },
    },
    // Booking Form
    booking: {
      tagline: "Prenota il Tuo Deposito",
      title: "Riserva il Tuo Posto",
      subtitle: "Inserisci i tuoi dati e penseremo a tutto noi",
      contactInfo: "Informazioni di Contatto",
      fullName: "Nome Completo",
      fullNamePlaceholder: "Inserisci il tuo nome completo",
      email: "Indirizzo Email",
      emailPlaceholder: "tua@email.com",
      mobile: "Numero di Cellulare",
      whatsapp: "Numero WhatsApp",
      phonePlaceholder: "+39 xxx xxx xxxx",
      storageDetails: "Dettagli Deposito",
      numberOfBags: "Numero di Bagagli",
      selectBags: "Seleziona numero di bagagli",
      bag: "bagaglio",
      bags: "bagagli",
      pickupDate: "Data Ritiro",
      pickupTime: "Ora Ritiro",
      dropoffDate: "Data Consegna",
      dropoffTime: "Ora Consegna",
      selectDate: "Seleziona data",
      selectTime: "Seleziona ora",
      paymentMethod: "Metodo di Pagamento",
      payAtLocation: "Paga in Loco",
      payAtLocationDesc: "Pagamento in contanti o carta all'arrivo",
      totalPrice: "Prezzo Totale",
      calculating: "Calcolo prezzo...",
      confirmBooking: "Conferma Prenotazione",
      processing: "Elaborazione...",
      pricingType: "Tipo di Tariffa",
      pricingAuto: "Auto",
      pricingAutoDesc: "Migliore tariffa",
      perHour: "all'ora",
      perDay: "al giorno",
      sameAsMobile: "Stesso del cellulare",
    },
    // Location Section
    location: {
      tagline: "Trovaci",
      title: "La Nostra Posizione",
      openMaps: "Apri in Google Maps",
    },
    // Footer
    footer: {
      description: "Il tuo servizio di deposito bagagli affidabile nel cuore di Bari, Italia.",
      contact: "Contatti",
      pricing: "Prezzi",
      perDay: "per bagaglio al giorno",
      perHour: "per bagaglio all'ora",
      hourlyNote: "*Tariffa oraria per deposito giornaliero",
      rights: "Tutti i diritti riservati.",
    },
    // Booking Confirmation
    confirmation: {
      title: "Prenotazione Confermata!",
      subtitle: "Sei pronto per esplorare Bari a mani libere!",
      reference: "Riferimento Prenotazione",
      customerDetails: "Dettagli Cliente",
      storageDetails: "Dettagli Deposito",
      numberOfBags: "Numero di Bagagli",
      pricingType: "Tipo Tariffa",
      pickup: "Ritiro",
      dropoff: "Consegna",
      totalPrice: "Prezzo Totale",
      payAtLocation: "Paga in Loco",
      location: "Posizione",
      openMaps: "Apri in Google Maps",
      shareWhatsApp: "Condividi la prenotazione via WhatsApp",
      contactWhatsApp: "Contattaci su WhatsApp",
      backHome: "Torna alla Home",
      notFound: "Prenotazione Non Trovata",
      notFoundDesc: "Non abbiamo trovato questa prenotazione. Controlla l'ID.",
      loading: "Caricamento prenotazione...",
    },
    // Admin
    admin: {
      login: {
        title: "Bentornato",
        subtitle: "Accedi per gestire le prenotazioni",
        createTitle: "Crea Account",
        createSubtitle: "Configura il tuo account admin",
        fullName: "Nome Completo",
        email: "Indirizzo Email",
        password: "Password",
        signIn: "Accedi",
        createAccount: "Crea Account",
        signingIn: "Accesso...",
        creating: "Creazione account...",
        needAccount: "Hai bisogno di un account? Registrati",
        haveAccount: "Hai già un account? Accedi",
        backToWebsite: "Torna al Sito",
        portal: "Portale Admin",
      },
      dashboard: {
        title: "Dashboard",
        bookings: "Prenotazioni",
        settings: "Impostazioni",
        overview: "Panoramica della tua attività",
        manageBookings: "Gestisci tutte le prenotazioni",
        configureSettings: "Configura la tua attività",
        refresh: "Aggiorna",
        totalBookings: "Prenotazioni Totali",
        pending: "In Attesa",
        confirmed: "Confermate",
        revenue: "Ricavi",
        recentBookings: "Prenotazioni Recenti",
        allBookings: "Tutte le Prenotazioni",
        viewAll: "Vedi Tutto",
        filterStatus: "Filtra per stato",
        all: "Tutte",
        completed: "Completate",
        cancelled: "Cancellate",
        customer: "Cliente",
        price: "Prezzo",
        status: "Stato",
        actions: "Azioni",
        noBookings: "Nessuna prenotazione trovata",
        bookingDetails: "Dettagli Prenotazione",
        contactWhatsApp: "Contatta via WhatsApp",
        updateStatus: "Aggiorna Stato",
        confirm: "Conferma",
        complete: "Completa",
        cancel: "Cancella",
        loggedInAs: "Connesso come",
        logout: "Esci",
        companyLogo: "Logo Aziendale",
        currentLogo: "Logo Attuale",
        noLogo: "Nessun logo",
        uploadInfo: "PNG, JPEG, WebP o SVG. Max 2MB.",
        removeLogo: "Rimuovi Logo",
        logoUploaded: "Logo caricato con successo!",
        logoDeleted: "Logo eliminato",
      },
    },
    // Common
    common: {
      hours: "ore",
      hour: "ora",
      days: "giorni",
      day: "giorno",
      daily: "giornaliero",
      hourly: "orario",
    },
  },
  es: {
    // Navigation
    nav: {
      features: "Características",
      location: "Ubicación",
      admin: "Admin",
      bookNow: "Reservar Ahora",
    },
    // Hero Section
    hero: {
      tagline: "Consigna de Equipaje de Confianza en Bari",
      title1: "Viaja Ligero,",
      title2: "Explora Libremente",
      description: "Consigna de equipaje segura en el corazón de Bari. Deja tus maletas, recupera tu libertad y descubre la belleza de Puglia.",
      learnMore: "Saber Más",
      perBagDay: "por bolsa/día",
      perBagHour: "por bolsa/hora",
      support: "Soporte",
    },
    // Features Section
    features: {
      tagline: "Por Qué Elegirnos",
      title: "Seguro, Simple y Protegido",
      secure: {
        title: "100% Seguro",
        description: "Tu equipaje se guarda en una instalación cerrada y monitoreada con cobertura de seguro para tu tranquilidad.",
      },
      flexible: {
        title: "Horarios Flexibles",
        description: "Guarda tus maletas por horas o días. Paga solo por el tiempo que necesites con nuestros precios flexibles.",
      },
      central: {
        title: "Ubicación Central",
        description: "Ubicado en el corazón de Bari, a pocos minutos de la estación de tren y del casco antiguo.",
      },
    },
    // Booking Form
    booking: {
      tagline: "Reserva Tu Consigna",
      title: "Reserva Tu Lugar",
      subtitle: "Ingresa tus datos y nosotros nos encargamos del resto",
      contactInfo: "Información de Contacto",
      fullName: "Nombre Completo",
      fullNamePlaceholder: "Ingresa tu nombre completo",
      email: "Correo Electrónico",
      emailPlaceholder: "tu@email.com",
      mobile: "Número de Móvil",
      whatsapp: "Número de WhatsApp",
      phonePlaceholder: "+39 xxx xxx xxxx",
      storageDetails: "Detalles de Consigna",
      numberOfBags: "Número de Maletas",
      selectBags: "Selecciona número de maletas",
      bag: "maleta",
      bags: "maletas",
      pickupDate: "Fecha de Recogida",
      pickupTime: "Hora de Recogida",
      dropoffDate: "Fecha de Entrega",
      dropoffTime: "Hora de Entrega",
      selectDate: "Seleccionar fecha",
      selectTime: "Seleccionar hora",
      paymentMethod: "Método de Pago",
      payAtLocation: "Pagar en el Lugar",
      payAtLocationDesc: "Pago en efectivo o tarjeta al llegar",
      totalPrice: "Precio Total",
      calculating: "Calculando precio...",
      confirmBooking: "Confirmar Reserva",
      processing: "Procesando...",
      pricingType: "Tipo de Tarifa",
      pricingAuto: "Auto",
      pricingAutoDesc: "Mejor tarifa",
      perHour: "por hora",
      perDay: "por día",
      sameAsMobile: "Igual que móvil",
    },
    // Location Section
    location: {
      tagline: "Encuéntranos",
      title: "Nuestra Ubicación",
      openMaps: "Abrir en Google Maps",
    },
    // Footer
    footer: {
      description: "Tu servicio de consigna de equipaje de confianza en el corazón de Bari, Italia.",
      contact: "Contacto",
      pricing: "Precios",
      perDay: "por maleta por día",
      perHour: "por maleta por hora",
      hourlyNote: "*Tarifa por hora para consigna del mismo día",
      rights: "Todos los derechos reservados.",
    },
    // Booking Confirmation
    confirmation: {
      title: "¡Reserva Confirmada!",
      subtitle: "¡Estás listo para explorar Bari con las manos libres!",
      reference: "Referencia de Reserva",
      customerDetails: "Datos del Cliente",
      storageDetails: "Detalles de Consigna",
      numberOfBags: "Número de Maletas",
      pricingType: "Tipo de Tarifa",
      pickup: "Recogida",
      dropoff: "Entrega",
      totalPrice: "Precio Total",
      payAtLocation: "Pagar en el Lugar",
      location: "Ubicación",
      openMaps: "Abrir en Google Maps",
      shareWhatsApp: "Comparte tu reserva por WhatsApp",
      contactWhatsApp: "Contáctanos por WhatsApp",
      backHome: "Volver al Inicio",
      notFound: "Reserva No Encontrada",
      notFoundDesc: "No pudimos encontrar esta reserva. Por favor verifica el ID.",
      loading: "Cargando tu reserva...",
    },
    // Admin
    admin: {
      login: {
        title: "Bienvenido",
        subtitle: "Inicia sesión para gestionar reservas",
        createTitle: "Crear Cuenta",
        createSubtitle: "Configura tu cuenta de administrador",
        fullName: "Nombre Completo",
        email: "Correo Electrónico",
        password: "Contraseña",
        signIn: "Iniciar Sesión",
        createAccount: "Crear Cuenta",
        signingIn: "Iniciando sesión...",
        creating: "Creando cuenta...",
        needAccount: "¿Necesitas una cuenta? Regístrate",
        haveAccount: "¿Ya tienes una cuenta? Inicia sesión",
        backToWebsite: "Volver al Sitio",
        portal: "Portal de Admin",
      },
      dashboard: {
        title: "Panel",
        bookings: "Reservas",
        settings: "Configuración",
        overview: "Resumen de tu negocio",
        manageBookings: "Gestiona todas las reservas",
        configureSettings: "Configura tu negocio",
        refresh: "Actualizar",
        totalBookings: "Reservas Totales",
        pending: "Pendientes",
        confirmed: "Confirmadas",
        revenue: "Ingresos",
        recentBookings: "Reservas Recientes",
        allBookings: "Todas las Reservas",
        viewAll: "Ver Todo",
        filterStatus: "Filtrar por estado",
        all: "Todas",
        completed: "Completadas",
        cancelled: "Canceladas",
        customer: "Cliente",
        price: "Precio",
        status: "Estado",
        actions: "Acciones",
        noBookings: "No se encontraron reservas",
        bookingDetails: "Detalles de Reserva",
        contactWhatsApp: "Contactar por WhatsApp",
        updateStatus: "Actualizar Estado",
        confirm: "Confirmar",
        complete: "Completar",
        cancel: "Cancelar",
        loggedInAs: "Conectado como",
        logout: "Cerrar Sesión",
        companyLogo: "Logo de Empresa",
        currentLogo: "Logo Actual",
        noLogo: "Sin logo",
        uploadInfo: "PNG, JPEG, WebP o SVG. Máx 2MB.",
        removeLogo: "Eliminar Logo",
        logoUploaded: "¡Logo subido con éxito!",
        logoDeleted: "Logo eliminado",
      },
    },
    // Common
    common: {
      hours: "horas",
      hour: "hora",
      days: "días",
      day: "día",
      daily: "diario",
      hourly: "por hora",
    },
  },
  ro: {
    // Navigation
    nav: {
      features: "Caracteristici",
      location: "Locație",
      admin: "Admin",
      bookNow: "Rezervă Acum",
    },
    // Hero Section
    hero: {
      tagline: "Depozitare Bagaje de Încredere în Bari",
      title1: "Călătorește Ușor,",
      title2: "Explorează Liber",
      description: "Depozitare sigură a bagajelor în inima orașului Bari. Lasă bagajele, câștigă libertatea și descoperă frumusețea Pugliei.",
      learnMore: "Află Mai Multe",
      perBagDay: "pe geantă/zi",
      perBagHour: "pe geantă/oră",
      support: "Suport",
    },
    // Features Section
    features: {
      tagline: "De Ce Să Ne Alegi",
      title: "Sigur, Simplu și Securizat",
      secure: {
        title: "100% Sigur",
        description: "Bagajele tale sunt păstrate într-o facilitate încuiată și monitorizată cu acoperire de asigurare.",
      },
      flexible: {
        title: "Ore Flexibile",
        description: "Depozitează bagajele pentru ore sau zile. Plătește doar pentru timpul de care ai nevoie.",
      },
      central: {
        title: "Locație Centrală",
        description: "Situat în inima orașului Bari, la câteva minute de gară și centrul istoric.",
      },
    },
    // Booking Form
    booking: {
      tagline: "Rezervă Depozitarea",
      title: "Rezervă-ți Locul",
      subtitle: "Completează detaliile și noi ne ocupăm de rest",
      contactInfo: "Informații de Contact",
      fullName: "Nume Complet",
      fullNamePlaceholder: "Introdu numele complet",
      email: "Adresă Email",
      emailPlaceholder: "email@exemplu.com",
      mobile: "Număr Mobil",
      whatsapp: "Număr WhatsApp",
      phonePlaceholder: "+39 xxx xxx xxxx",
      storageDetails: "Detalii Depozitare",
      numberOfBags: "Număr de Bagaje",
      selectBags: "Selectează numărul de bagaje",
      bag: "geantă",
      bags: "genți",
      pickupDate: "Data Ridicării",
      pickupTime: "Ora Ridicării",
      dropoffDate: "Data Predării",
      dropoffTime: "Ora Predării",
      selectDate: "Selectează data",
      selectTime: "Selectează ora",
      paymentMethod: "Metodă de Plată",
      payAtLocation: "Plată la Locație",
      payAtLocationDesc: "Plată în numerar sau card la sosire",
      totalPrice: "Preț Total",
      calculating: "Se calculează prețul...",
      confirmBooking: "Confirmă Rezervarea",
      processing: "Se procesează...",
      pricingType: "Tip Tarifare",
      pricingAuto: "Auto",
      pricingAutoDesc: "Cel mai bun tarif",
      perHour: "pe oră",
      perDay: "pe zi",
      sameAsMobile: "La fel ca mobilul",
    },
    // Location Section
    location: {
      tagline: "Găsește-ne",
      title: "Locația Noastră",
      openMaps: "Deschide în Google Maps",
    },
    // Footer
    footer: {
      description: "Serviciul tău de depozitare bagaje de încredere în inima orașului Bari, Italia.",
      contact: "Contact",
      pricing: "Prețuri",
      perDay: "pe geantă pe zi",
      perHour: "pe geantă pe oră",
      hourlyNote: "*Tarif orar pentru depozitare în aceeași zi",
      rights: "Toate drepturile rezervate.",
    },
    // Booking Confirmation
    confirmation: {
      title: "Rezervare Confirmată!",
      subtitle: "Ești gata să explorezi Bari cu mâinile libere!",
      reference: "Referință Rezervare",
      customerDetails: "Detalii Client",
      storageDetails: "Detalii Depozitare",
      numberOfBags: "Număr de Bagaje",
      pricingType: "Tip Tarifare",
      pickup: "Ridicare",
      dropoff: "Predare",
      totalPrice: "Preț Total",
      payAtLocation: "Plată la Locație",
      location: "Locație",
      openMaps: "Deschide în Google Maps",
      shareWhatsApp: "Distribuie rezervarea pe WhatsApp",
      contactWhatsApp: "Contactează-ne pe WhatsApp",
      backHome: "Înapoi Acasă",
      notFound: "Rezervare Negăsită",
      notFoundDesc: "Nu am putut găsi această rezervare. Verifică ID-ul.",
      loading: "Se încarcă rezervarea...",
    },
    // Admin
    admin: {
      login: {
        title: "Bine ai Revenit",
        subtitle: "Conectează-te pentru a gestiona rezervările",
        createTitle: "Creează Cont",
        createSubtitle: "Configurează contul de admin",
        fullName: "Nume Complet",
        email: "Adresă Email",
        password: "Parolă",
        signIn: "Conectare",
        createAccount: "Creează Cont",
        signingIn: "Se conectează...",
        creating: "Se creează contul...",
        needAccount: "Ai nevoie de cont? Înregistrează-te",
        haveAccount: "Ai deja cont? Conectează-te",
        backToWebsite: "Înapoi la Site",
        portal: "Portal Admin",
      },
      dashboard: {
        title: "Panou",
        bookings: "Rezervări",
        settings: "Setări",
        overview: "Prezentare generală",
        manageBookings: "Gestionează toate rezervările",
        configureSettings: "Configurează afacerea",
        refresh: "Actualizează",
        totalBookings: "Total Rezervări",
        pending: "În Așteptare",
        confirmed: "Confirmate",
        revenue: "Venituri",
        recentBookings: "Rezervări Recente",
        allBookings: "Toate Rezervările",
        viewAll: "Vezi Tot",
        filterStatus: "Filtrează după status",
        all: "Toate",
        completed: "Finalizate",
        cancelled: "Anulate",
        customer: "Client",
        price: "Preț",
        status: "Status",
        actions: "Acțiuni",
        noBookings: "Nu s-au găsit rezervări",
        bookingDetails: "Detalii Rezervare",
        contactWhatsApp: "Contact prin WhatsApp",
        updateStatus: "Actualizează Status",
        confirm: "Confirmă",
        complete: "Finalizează",
        cancel: "Anulează",
        loggedInAs: "Conectat ca",
        logout: "Deconectare",
        companyLogo: "Logo Companie",
        currentLogo: "Logo Actual",
        noLogo: "Fără logo",
        uploadInfo: "PNG, JPEG, WebP sau SVG. Max 2MB.",
        removeLogo: "Șterge Logo",
        logoUploaded: "Logo încărcat cu succes!",
        logoDeleted: "Logo șters",
      },
    },
    // Common
    common: {
      hours: "ore",
      hour: "oră",
      days: "zile",
      day: "zi",
      daily: "zilnic",
      hourly: "pe oră",
    },
  },
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
